import { useCallback, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ApiGuide, copyText as copy } from "@/components/ServiceApiGuide";
import { MarketFeedPanel } from "@/components/MarketFeedPanel";
import { toast } from "sonner";
import { KeyRound, Copy, Ban, Trash2 } from "lucide-react";

const TH = "px-3 py-2 text-left text-[11px] uppercase tracking-wider font-semibold text-muted-foreground";
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

function NewKey({ created, onDismiss }) {
  return (
    <div className="rounded-md border-2 border-amber-400 bg-amber-50 p-3 space-y-2" data-testid="svc-apikey-new">
      <p className="text-xs font-semibold text-amber-900">Copy this key now — it is shown only once. Store it in the DMS job's secret store, never in a browser.</p>
      <div className="flex items-center gap-2">
        <code className="flex-1 break-all rounded bg-white px-2 py-1.5 text-xs font-mono border" data-testid="svc-apikey-value">{created.key}</code>
        <Button size="sm" variant="outline" onClick={() => copy(created.key, "API key")} data-testid="svc-apikey-copy-btn"><Copy size={14} className="mr-1" />Copy</Button>
      </div>
      <ApiGuide apiKey={created.key} />
      <Button size="sm" variant="ghost" onClick={onDismiss} data-testid="svc-apikey-dismiss-btn">I've saved it</Button>
    </div>
  );
}

export function ServiceApiKeys() {
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState("");
  const [expires, setExpires] = useState("");
  const [created, setCreated] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => { api.get("/admin/api-keys").then(({ data }) => setKeys(data)).catch((e) => toast.error(apiError(e))); }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/api-keys", { name: name.trim(), expires_at: expires || null });
      setCreated(data); setName(""); setExpires(""); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const revoke = async (k) => {
    if (!window.confirm(`Revoke "${k.name}"? Any job using it will start getting 401 immediately.`)) return;
    try { await api.post(`/admin/api-keys/${k.key_id}/revoke`); toast.success(`${k.name} revoked`); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (k) => {
    if (!window.confirm(`Remove "${k.name}" from the list?`)) return;
    try { await api.delete(`/admin/api-keys/${k.key_id}`); load(); } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="border bg-card rounded-md p-4 space-y-3" data-testid="svc-apikeys-panel">
      <div>
        <h3 className="font-heading text-sm font-semibold flex items-center gap-1.5"><KeyRound size={14} /> API keys — DMS files & market-share push</h3>
        <p className="text-xs text-muted-foreground">Bearer tokens for machine jobs: the DMS job pushes the 4 daily service files to <code className="font-mono">POST /api/integrations/dms/upload</code>; the market-share job pushes <code className="font-mono">MARKET_SHARE_YYYY-MM-DD.xlsx</code> to <code className="font-mono">POST /api/integrations/market-share/upload</code> (block below). One key may serve both, a dedicated key per job is recommended.</p>
      </div>
      <ApiGuide idPrefix="svc-apiguide" />
      <div className="flex flex-wrap items-center gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder='e.g. "DMS nightly job"' className="h-9 w-56 text-sm" data-testid="svc-apikey-name-input" />
        <input type="date" value={expires} onChange={(e) => setExpires(e.target.value)} className="h-9 border rounded px-2 text-sm bg-background" title="Optional expiry" data-testid="svc-apikey-expiry-input" />
        <Button onClick={create} disabled={busy || !name.trim()} data-testid="svc-apikey-create-btn">Create key</Button>
      </div>
      {created && <NewKey created={created} onDismiss={() => setCreated(null)} />}
      <div className="overflow-auto border rounded-md">
        <table className="w-full text-sm">
          <thead className="border-b"><tr>{["Name", "Key", "Created", "Expires", "Last used", "Uses", "Status", ""].map((h, i) => <th key={i} className={TH}>{h}</th>)}</tr></thead>
          <tbody>
            {keys.map((k) => {
              const status = !k.active ? "revoked" : k.expired ? "expired" : "active";
              return (
                <tr key={k.key_id} className={`border-b last:border-0 ${status !== "active" ? "text-muted-foreground" : ""}`} data-testid={`svc-apikey-row-${k.key_id}`}>
                  <td className="px-3 py-1.5 font-semibold">{k.name}<div className="text-[10px] font-normal text-muted-foreground">by {k.created_by}</div></td>
                  <td className="px-3 py-1.5 font-mono text-xs">{k.prefix}_…</td>
                  <td className="px-3 py-1.5 text-xs whitespace-nowrap">{fmt(k.created_at)}</td>
                  <td className="px-3 py-1.5 text-xs whitespace-nowrap">{k.expires_at ? fmt(k.expires_at) : "never"}</td>
                  <td className="px-3 py-1.5 text-xs whitespace-nowrap">{fmt(k.last_used_at)}</td>
                  <td className="px-3 py-1.5 num text-xs">{k.uses}</td>
                  <td className="px-3 py-1.5"><span className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${status === "active" ? "bg-emerald-100 text-emerald-800" : "bg-secondary"}`} data-testid={`svc-apikey-status-${k.key_id}`}>{status}</span></td>
                  <td className="px-3 py-1.5 text-right whitespace-nowrap">
                    {status === "active"
                      ? <Button size="sm" variant="ghost" className="text-red-600 h-7" onClick={() => revoke(k)} data-testid={`svc-apikey-revoke-${k.key_id}`}><Ban size={13} className="mr-1" />Revoke</Button>
                      : <Button size="sm" variant="ghost" className="h-7 text-muted-foreground" onClick={() => remove(k)} title="Remove from list" data-testid={`svc-apikey-delete-${k.key_id}`}><Trash2 size={13} /></Button>}
                  </td>
                </tr>
              );
            })}
            {keys.length === 0 && <tr><td colSpan={8} className="px-3 py-6 text-center text-xs text-muted-foreground" data-testid="svc-apikeys-empty">No API keys yet — create one for the DMS job.</td></tr>}
          </tbody>
        </table>
      </div>
      <MarketFeedPanel apiKey={created?.key} />
    </div>
  );
}
